import { validateEmail } from "../../utils/validationUtils";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  SPOC_EMPTY_FORM,
  DEFAULT_MASTER_ADMIN_EMAIL,
  getSpocRoleOptions,
  isMasterAdminUser,
} from "./SpocFields";
import { getUserData } from "../../utils/authnicate";
import { createSpocAction } from "../../actions/createSpocAction";
import { updateSpocAction } from "../../actions/updateSpocAction";
import { fetchSpocListAction } from "../../actions/fetchSpocListAction";
import { toggleSpocStatusAction } from "../../actions/toggleSpocStatusAction";

const userdata = getUserData();

export const PAGE_SIZE = 10;

const validateSpocForm = (form, isEdit = false, isMasterAdmin = false) => {
  const errors = {};
  const isSpocRole =
    String(form?.role || "")
      .trim()
      .toLowerCase() === "spoc";
  const isLobDeptRequired = !isMasterAdmin || isSpocRole;

  if (isEdit) {
    if (isLobDeptRequired) {
      if (!form.lob?.trim()) {
        errors.lob = "Entity LOB Type is required";
      }
      if (!Array.isArray(form.departments) || form.departments.length === 0) {
        errors.departments = "At least one department is required";
      }
    }
    return errors;
  }

  // ── Create validation ─────────────────────────────────────────────────────
  if (!form.employeeName?.trim()) {
    errors.employeeName = "Employee Name is required";
  } else if (form.employeeName.trim().length < 3) {
    errors.employeeName = "Employee Name must be at least 3 characters";
  }

  if (!form.email?.trim()) {
    errors.email = "Email ID is required";
  } else {
    const emailResult = validateEmail(form.email);
    if (emailResult !== true) {
      errors.email = emailResult;
    }
  }

  if (!form.password?.trim()) {
    errors.password = "Password is required";
  } else if (form.password.trim().length < 8) {
    errors.password = "Password must be at least 8 characters";
  }

  if (isLobDeptRequired) {
    if (!form.lob?.trim()) {
      errors.lob = "Entity LOB Type is required";
    }

    if (!Array.isArray(form.departments) || form.departments.length === 0) {
      errors.departments = "At least one department is required";
    }
  }

  if (!form.role?.trim()) {
    errors.role = "Role is required";
  }

  return errors;
};

export const useSpocManagement = () => {
  const loggedInUser = getUserData();
  const loggedInUserEmail =
    loggedInUser?.email || sessionStorage.getItem("otpEmail") || "";
  const isMasterAdmin = isMasterAdminUser(loggedInUserEmail);
  const roleOptions = getSpocRoleOptions(loggedInUserEmail);

  const [spocList, setSpocList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedSpoc, setSelectedSpoc] = useState(null);
  const [formData, setFormData] = useState({ ...SPOC_EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);

  const debounceRef = useRef(null);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const loadList = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    try {
      const result = await fetchSpocListAction({
        searchValue: search?.trim() || undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      });

      if (result.success) {
        setSpocList(result.data);
        setTotalRecords(result.totalRecords);
        const calculatedTotalPages =
          Math.ceil(result.totalRecords / PAGE_SIZE) || 1;
        if (page > calculatedTotalPages) {
          setCurrentPage(calculatedTotalPages);
        }
      } else {
        toast.error(result.message || "Failed to load SPOC list");
        setSpocList([]);
        setTotalRecords(0);
      }
    } catch {
      toast.error("Failed to load SPOC list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList(currentPage, searchQueryRef.current);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    searchQueryRef.current = value;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage((prev) => {
        if (prev === 1) {
          loadList(1, value);
          return 1;
        }
        return 1;
      });
    }, 400);
  };

  const openAddModal = () => {
    clearTimeout(debounceRef.current);
    setFormData({ ...SPOC_EMPTY_FORM });
    setFormErrors({});
    setSelectedSpoc(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const openViewModal = (spoc) => {
    setSelectedSpoc(spoc);
    setFormData({ ...spoc });
    setFormErrors({});
    setModalMode("view");
    setModalOpen(true);
  };

  const openEditModal = (spoc) => {
    setSelectedSpoc(spoc);
    setFormData({ ...spoc, password: "" });
    setFormErrors({});
    setModalMode("edit");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData({ ...SPOC_EMPTY_FORM });
    setFormErrors({});
    setSelectedSpoc(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "lob") {
        next.departments = []; // reset departments when LOB changes
      }
      if (name === "role" && value?.toLowerCase() !== "spoc") {
        next.lob = "";
        next.departments = [];
      }
      return next;
    });

    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (name === "lob") delete next.departments;
      if (name === "role" && value?.toLowerCase() !== "spoc") {
        delete next.lob;
        delete next.departments;
      }
      return next;
    });
  };

  // Toggle a single department in/out of the departments array
  const handleDeptToggle = (deptValue) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.departments) ? prev.departments : [];
      const next = current.includes(deptValue)
        ? current.filter((d) => d !== deptValue)
        : [...current, deptValue];
      return { ...prev, departments: next };
    });
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.departments;
      return next;
    });
  };

  const handleSubmit = async () => {
    const isEdit = modalMode === "edit";
    const errors = validateSpocForm(formData, isEdit, isMasterAdmin);
    if (Object.keys(errors).length > 0) {
      console.warn(
        "[SpocManagement] Validation failed:",
        errors,
        "| formData:",
        formData,
      );
      setFormErrors(errors);
      return;
    }

    // console.log(
    //   "[SpocManagement] handleSubmit | mode:",
    //   modalMode,
    //   "| formData:",
    //   formData,
    // );
    setSaving(true);
    try {
      if (isEdit) {
        const result = await updateSpocAction(selectedSpoc.id, formData);
        if (!result.success) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message || "SPOC updated successfully!");
      } else {
        const result = await createSpocAction(formData);
        if (!result.success) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message || "SPOC created successfully!");
      }
      closeModal();
      if (!isEdit) {
        setSearchQuery("");
        searchQueryRef.current = "";
        setCurrentPage(1);
        await loadList(1, "");
      } else {
        await loadList(currentPage, searchQuery);
      }
    } catch {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openStatusModal = (spoc) => {
    setStatusTarget(spoc);
    setStatusValue(spoc.isActive !== false);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setStatusTarget(null);
  };

  const handleStatusSubmit = async () => {
    if (!statusTarget) return;

    setStatusLoading(true);
    console.log(
      `[SpocManagement] toggleStatus | id=${statusTarget.id} | isActive=${statusValue}`,
    );
    try {
      const result = await toggleSpocStatusAction(statusTarget.id, statusValue);
      if (!result.success) {
        toast.error(result.message);
        return;
      }
      toast.success(
        result.message ||
          `SPOC ${statusValue ? "activated" : "deactivated"} successfully!`,
      );
      closeStatusModal();
      await loadList(currentPage, searchQuery);
    } catch {
      toast.error("Failed to update SPOC status");
    } finally {
      setStatusLoading(false);
    }
  };

  return {
    spocList,
    totalRecords,
    currentPage,
    setCurrentPage,
    pageSize: PAGE_SIZE,
    loading,
    searchQuery,
    handleSearchChange,
    loadList,
    modalOpen,
    modalMode,
    formData,
    formErrors,
    saving,
    selectedSpoc,
    openAddModal,
    openViewModal,
    openEditModal,
    closeModal,
    handleFieldChange,
    handleDeptToggle,
    handleSubmit,
    statusModalOpen,
    statusTarget,
    statusValue,
    setStatusValue,
    statusLoading,
    openStatusModal,
    closeStatusModal,
    handleStatusSubmit,
    roleOptions,
    isMasterAdmin,
    loggedInUserEmail,
  };
};
